import React, { Suspense, useEffect, useMemo, useRef } from 'react';
import { useChannelContext } from 'scbk-services/channel';
import { Channel } from 'scbk-services/config';
import { useRestContext } from 'scbk-services/http';
import { useAppContextStore } from 'scbk-services/shared-context/stores/appContextStore';
import { useRouterHistory } from '../stores/routerHistoryStore';
import { useRouterRegistry } from '../stores/routerRegistryStore';

/**
 * MenuRoute Props
 */
interface Props {
    id: string;
    path: string;
}

interface LoadingComponentProps {
    loading: boolean;
    className?: string;
    message?: string;
    type?: 'shift' | 'upleft';
}
type LoadingProps = LoadingComponentProps & {
    appendToRoot?: boolean;
    prefixClassName?: string;
    message?: string;
};

/**
 * MenuRoute
 * 메뉴트리에 등록된 메뉴정보를 Route에 등록 시 Element에 MenuRoute로 등록한다.
 * TODO: 삭제
 */
const MenuRoute = ({}: Props) => {
    return null;
};

/**
 * pageContainer Props
 */
interface pageContainerProps {
    loadingComponent: React.ComponentType<LoadingProps>;
}

/**
 *
 * @returns
 */
const PageContainer = React.memo(({ loadingComponent: LoadingComponent }: pageContainerProps) => {
    const { channel } = useChannelContext();
    const findModuleRouteObjectsByPageId = useRouterRegistry(
        (state) => state.findModuleRouteObjectsByPageId,
    );

    const clearCurrentRouteObject = useRouterRegistry((state) => state.clearCurrentRouteObject);
    const setCurrentRouteObject = useRouterRegistry((state) => state.setCurrentRouteObject);
    const clearCurrentRouteObjectConfig = useRouterRegistry(
        (state) => state.clearCurrentRouteObjectConfig,
    );
    const setCurrentRouteObjectConfig = useRouterRegistry(
        (state) => state.setCurrentRouteObjectConfig,
    );
    const initialized = useAppContextStore((state) => state.context.initialized);

    // API 호출시 screen id header 적재를 위한 store
    const { updateContext: updateRestContext } = useRestContext();

    // route history stack
    const stack = useRouterHistory((state) => state.stack);

    /**
     * route history stack에 맨 마지막 route 정보를 획득한다.
     */
    const current = useMemo(() => {
        return stack?.[stack.length - 1];
    }, [stack]);

    const lastCurrentRef = useRef<typeof current>();
    const currentRouteIdRef = useRef<string>();
    const resolvedCurrent = current ?? lastCurrentRef.current;

    /**
     * 모듈별로 등록된 routeObject 객체를 검색하여 반환한다.
     */
    const currentRouteObject = useMemo(() => {
        if (!current) {
            return;
        }
        return findModuleRouteObjectsByPageId(current.id);
    }, [resolvedCurrent?.id, resolvedCurrent?.path]);

    useEffect(() => {
        if (!current) {
            return;
        }

        //깊은복사
        lastCurrentRef.current = current;
    }, [current]);

    //********************************************************************
    // routeObject, routeObjectConfig 확정
    //********************************************************************
    useEffect(() => {
        if (!currentRouteObject) {
            clearCurrentRouteObject();
            clearCurrentRouteObjectConfig();
            return;
        }

        if (currentRouteIdRef.current === currentRouteObject.id) {
            return;
        }

        currentRouteIdRef.current = currentRouteObject.id;

        //********************************************************************
        // api 호출시 header에 screen id set
        //********************************************************************
        const screenInfo = {
            screenId: currentRouteObject.id,
        };
        updateRestContext({ ...screenInfo });

        setCurrentRouteObject(currentRouteObject);

        const init = async () => {
            const resolve = await currentRouteObject.resolve();
            setCurrentRouteObjectConfig(resolve);
        };

        init();
    }, [currentRouteObject]);

    useEffect(() => {
        console.log('# PageContainer MOUNT');
        return () => {
            console.log('# PageContainer UNMOUNT');
        };
    }, []);

    //********************************************************************
    //앱 초기화가 되지 않는 경우 로딩 컴포넌트 처리
    //********************************************************************
    if (!initialized) {
        return <LoadingComponent loading={channel !== Channel.MB} />;
    }

    if (!current || current.id === '' || !current.path || !currentRouteObject) {
        console.log(`# PageContainer current: ${current}, currentRouteObject:`, currentRouteObject);
        return <LoadingComponent loading={channel !== Channel.MB} />;
    }

    return (
        <Suspense fallback={channel === Channel.MB ? null : <LoadingComponent loading={true} />}>
            {currentRouteObject.element}
        </Suspense>
    );
});

export { MenuRoute, PageContainer };
